import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Switch from "@material-ui/core/Switch";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	row: {
		display: "flex",
		gap: theme.spacing(1),
		marginBottom: 0,
	},
	textField: {
		flex: 1,
	},
	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	// feat_384: un colaborador sin permiso recibe el número enmascarado
	// ("549351●●●●482"); es read-only en edición y no se reenvía al guardar.
	number: Yup.string()
		.matches(/^[\d●]*$/, i18n.t("contactModal.form.numberOnlyDigits"))
		.min(8, "Too Short!")
		.max(15, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		lastName: "",
		organization: "",
		contactTypeId: "",
		signatureMode: "default",
		// feat_316: override de notificación por contacto
		notificationPriority: "normal",
		notes: "",
		whatsappName: "",
	};

	const [contact, setContact] = useState(initialState);
	const [contactTypes, setContactTypes] = useState([]);
	const [aiBlocked, setAiBlocked] = useState(false);
	const [contactStatus, setContactStatus] = useState("pending");

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!open) return;
		api.get("/contact-types").then(({ data }) => setContactTypes(data)).catch(() => {});
	}, [open]);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(data);
					setAiBlocked(data.aiBlocked || false);
					setContactStatus(data.status || "pending");
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setAiBlocked(false);
		setContactStatus("pending");
	};

	const handleMoveStatus = async (newStatus) => {
		if (!contact?.id) return;
		try {
			await api.put(`/contacts/${contact.id}/status`, { status: newStatus });
			setContactStatus(newStatus);
			toast.success(i18n.t("contactModal.movedToast", { tab: i18n.t(`contactModal.moveTo_${newStatus}`) }));
		} catch (err) {
			toastError(err);
		}
	};

	const handleToggleAiBlocked = async () => {
		try {
			const newValue = !aiBlocked;
			await api.put(`/contacts/${contact.id}/intervention`, {
				aiBlocked: newValue,
			});
			setAiBlocked(newValue);
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveContact = async formValues => {
		const values = { ...formValues };
		if (typeof values.number === "string" && values.number.includes("●")) {
			delete values.number;
		}
		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, values);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", values);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								{/* Fila 1: Nombre | Apellido */}
								<div className={classes.row}>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.name")}
										name="name"
										autoFocus
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.lastName")}
										name="lastName"
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
								</div>

								{/* Fila 2: WhatsApp name (readonly) | Número */}
								<div className={classes.row}>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.whatsappName")}
										name="whatsappName"
										variant="outlined"
										margin="dense"
										className={classes.textField}
										InputProps={{ readOnly: true, style: { backgroundColor: "#f5f5f5" } }}
										inputProps={{ style: { color: "#888" } }}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.number")}
										name="number"
										error={touched.number && Boolean(errors.number)}
										helperText={(touched.number && errors.number) || i18n.t("contactModal.form.numberHelp")}
										placeholder="5493513456789"
										variant="outlined"
										margin="dense"
										className={classes.textField}
										onChange={e => {
											const onlyDigits = e.target.value.replace(/\D/g, "");
											setFieldValue("number", onlyDigits);
										}}
										InputProps={{ readOnly: !!contactId, style: contactId ? { backgroundColor: "#f5f5f5" } : {} }}
										inputProps={{ style: contactId ? { color: "#888" } : {}, inputMode: "numeric" }}
									/>
								</div>

								{/* Fila 3: Email | Organización */}
								<div className={classes.row}>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.company")}
										name="organization"
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
								</div>

								{/* Fila 4: Tipo de cliente */}
								<FormControl variant="outlined" margin="dense" fullWidth>
									<InputLabel>{i18n.t("contactModal.form.kind")}</InputLabel>
									<Field
										as={Select}
										name="contactTypeId"
										label={i18n.t("contactModal.form.kind")}
									>
										<MenuItem value="">Sin definir</MenuItem>
										{contactTypes.map(t => (
											<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
										))}
									</Field>
								</FormControl>

								{/* Fila 5: Modo de firma */}
								<FormControl variant="outlined" margin="dense" fullWidth>
									<InputLabel>{i18n.t("contactModal.signatureMode")}</InputLabel>
									<Field
										as={Select}
										name="signatureMode"
										label={i18n.t("contactModal.signatureMode")}
									>
										<MenuItem value="default">{i18n.t("contactModal.signatureModeDefault")}</MenuItem>
										<MenuItem value="always">{i18n.t("contactModal.signatureModeAlways")}</MenuItem>
										<MenuItem value="never">{i18n.t("contactModal.signatureModeNever")}</MenuItem>
									</Field>
								</FormControl>

								{/* feat_316 — Prioridad de notificación del contacto */}
								<FormControl variant="outlined" margin="dense" fullWidth>
									<InputLabel>{i18n.t("contactPriority.label")}</InputLabel>
									<Field
										as={Select}
										name="notificationPriority"
										label={i18n.t("contactPriority.label")}
									>
										<MenuItem value="normal">{i18n.t("contactPriority.normal")}</MenuItem>
										<MenuItem value="priority">{i18n.t("contactPriority.priority")}</MenuItem>
										<MenuItem value="muted">{i18n.t("contactPriority.muted")}</MenuItem>
									</Field>
									<FormHelperText>{i18n.t("contactPriority.help")}</FormHelperText>
								</FormControl>

								{/* Fila 6: Notas */}
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.notes")}
									name="notes"
									multiline
									rows={3}
									fullWidth
									margin="dense"
									variant="outlined"
								/>

								{/* Fila 6: Información adicional */}
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>

							{/* ── Bloquear IA ── */}
							<Grid item xs={12} style={{ marginTop: 8 }}>
								<Paper variant="outlined" style={{ padding: "8px 16px" }}>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<div>
											<Typography variant="subtitle2">Bloquear IA</Typography>
											<Typography variant="caption" color="textSecondary">
												Este contacto siempre será atendido por un asesor humano
											</Typography>
										</div>
										<Switch
											checked={aiBlocked}
											onChange={handleToggleAiBlocked}
											color="secondary"
											disabled={!contact?.id}
										/>
									</div>
								</Paper>
							</Grid>

							{/* Estado del contacto */}
							{contact?.id && (
								<Grid item xs={12} style={{ marginTop: 12 }}>
									<Paper variant="outlined" style={{ padding: "8px 16px" }}>
										<Typography variant="subtitle2" style={{ marginBottom: 8 }}>
											{i18n.t("contactModal.statusSection")}
										</Typography>
										<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
											{[
												{ status: "pending", labelKey: "contactModal.moveTo_pending" },
												{ status: "qualified", labelKey: "contactModal.moveTo_qualified" },
												{ status: "disqualified", labelKey: "contactModal.moveTo_disqualified" },
											].map(({ status, labelKey }) => (
												<Button
													key={status}
													size="small"
													variant={contactStatus === status ? "contained" : "outlined"}
													color={contactStatus === status ? "primary" : "default"}
													onClick={() => contactStatus !== status && handleMoveStatus(status)}
													disabled={contactStatus === status}
												>
													{i18n.t(labelKey)}
												</Button>
											))}
										</div>
									</Paper>
								</Grid>
							)}

							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
